import { Popover } from './Popover.js'
import { cn } from '../lib/cn.js'
import { Icon } from './Icon.js'

/**
 * Choosing the colour on a row that colours something else.
 *
 * This used to be a `<select>` per screen with eight options and its own copy
 * of the list, which got two things wrong. A dropdown of swatches shows one
 * colour at a time, so picking between two greens meant opening it twice; and
 * because the value had to be one of the eight, the imported ServiceM8 pastels
 * — which are most of the catalog — displayed as slate and were silently
 * replaced by whatever was picked next to them.
 *
 * So: a grid, which is how a palette is read, plus the browser's own picker for
 * anything the grid does not hold. A colour already in use that is not in the
 * palette keeps its place at the front of the grid rather than being
 * unrepresented.
 *
 * **What is stored is what gets drawn.** The board fills a booking with this
 * colour opaque and picks its ink from it, so a swatch is read at full strength
 * wherever it lands and a pastel is a pale card rather than a blank one. The
 * mid tones below are a mid tone because a palette wants an even ramp, not
 * because anything downstream is about to wash them out — it used to tint at
 * 15%, which is why every imported ServiceM8 pastel drew as white. See ADR 012.
 */

export interface Swatch {
  name: string
  hex: string
}

/** Six hues a row, warm to cool, then the greys and the deep tones. */
export const PALETTE: readonly Swatch[] = [
  { name: 'Red', hex: '#ef4444' },
  { name: 'Orange', hex: '#f97316' },
  { name: 'Amber', hex: '#f59e0b' },
  { name: 'Yellow', hex: '#eab308' },
  { name: 'Lime', hex: '#84cc16' },
  { name: 'Green', hex: '#22c55e' },

  { name: 'Emerald', hex: '#10b981' },
  { name: 'Teal', hex: '#14b8a6' },
  { name: 'Cyan', hex: '#06b6d4' },
  { name: 'Sky', hex: '#0ea5e9' },
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Indigo', hex: '#6366f1' },

  { name: 'Violet', hex: '#8b5cf6' },
  { name: 'Purple', hex: '#a855f7' },
  { name: 'Magenta', hex: '#d946ef' },
  { name: 'Pink', hex: '#ec4899' },
  { name: 'Rose', hex: '#f43f5e' },
  { name: 'Stone', hex: '#78716c' },

  { name: 'Slate', hex: '#64748b' },
  { name: 'Steel', hex: '#475569' },
  { name: 'Charcoal', hex: '#334155' },
  { name: 'Rust', hex: '#b45309' },
  { name: 'Forest', hex: '#166534' },
  { name: 'Navy', hex: '#1e40af' },
]

/** The default for a newly added row, so a run of them is not all one colour. */
export function nextColour(used: readonly string[]): string {
  const taken = new Set(used.map((hex) => hex.toLowerCase()))
  return (PALETTE.find((swatch) => !taken.has(swatch.hex))?.hex ?? PALETTE[0]!.hex)
}

function swatchName(hex: string): string {
  return PALETTE.find((swatch) => swatch.hex.toLowerCase() === hex.toLowerCase())?.name ?? 'Custom'
}

/**
 * The chip that opens the palette.
 *
 * `label` names what is being coloured, so a list of thirty of these does not
 * read as thirty controls called "Colour" to anything but the eye.
 */
export function ColourPicker({
  value,
  label = 'Colour',
  onChange,
}: {
  value: string
  label?: string
  onChange: (colour: string) => void
}) {
  const known = PALETTE.some((swatch) => swatch.hex.toLowerCase() === value.toLowerCase())

  return (
    <Popover
      width={216}
      title={`${label} — ${swatchName(value)}`}
      label={
        <>
          {/* The swatch is the whole point of the trigger and says nothing to a
              screen reader, so the name it would have had goes here. */}
          <span className="sr-only">{label}</span>
          <span
            aria-hidden
            className="size-4 rounded-sm ring-1 ring-inset ring-black/10"
            style={{ backgroundColor: value }}
          />
        </>
      }
    >
      {(close) => (
        <div className="p-1">
          <div className="grid grid-cols-6 gap-1">
            {/* A colour in use that the palette does not hold — every imported
                one — stays visible and selected instead of reading as slate. */}
            {known ? null : (
              <SwatchButton
                swatch={{ name: 'Current', hex: value }}
                selected
                onPick={() => close()}
              />
            )}

            {PALETTE.map((swatch) => (
              <SwatchButton
                key={swatch.hex}
                swatch={swatch}
                selected={swatch.hex.toLowerCase() === value.toLowerCase()}
                onPick={() => {
                  onChange(swatch.hex)
                  close()
                }}
              />
            ))}
          </div>

          {/* The way out of a fixed palette. Committed on change rather than on
              close, because the OS picker is its own window and closing it is
              not a gesture this panel sees. */}
          <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-xs text-ink-600 hover:bg-ink-50">
            <input
              type="color"
              value={value}
              aria-label={`${label} — anything else`}
              onChange={(event) => onChange(event.target.value)}
              className="size-5 cursor-pointer rounded-sm border-0 bg-transparent p-0"
            />
            Anything else
          </label>
        </div>
      )}
    </Popover>
  )
}

function SwatchButton({
  swatch,
  selected,
  onPick,
}: {
  swatch: Swatch
  selected: boolean
  onPick: () => void
}) {
  return (
    <button
      type="button"
      title={swatch.name}
      aria-label={swatch.name}
      aria-pressed={selected}
      onClick={onPick}
      style={{ backgroundColor: swatch.hex }}
      className={cn(
        'flex size-7 items-center justify-center rounded-md ring-1 ring-inset ring-black/10 transition-transform',
        'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ink-900',
        selected && 'ring-2 ring-ink-900',
      )}
    >
      {selected ? (
        <Icon name="check" className="size-3.5 text-white drop-shadow" strokeWidth={3} />
      ) : null}
    </button>
  )
}
